import { supabase } from "@/integrations/supabase/client";
import { sendActivityEmail } from "@/lib/activity-email";
import { invokeFunction } from "@/lib/invoke";
import {
  DEFAULT_EXPRESS_RATE_SCHEDULE,
  expressRateForDays,
  expressRepayAmount,
  impliedRatePercent,
  parseExpressRateSchedule,
  type ExpressRateBand,
} from "@/lib/express-rates";
import {
  DEFAULT_TERMS,
  ELITE_DEFAULT_LIMIT,
  EXPRESS_DEFAULT_LIMIT,
  EXPRESS_DOC_TYPES,
  EXPRESS_FIXED_RATE,
  EXPRESS_OPTIONAL_DOC_TYPES,
  TIER_LABEL,
  VERIFIED_DEFAULT_LIMIT,
  VERIFIED_EXTRA_DOC_TYPES,
  clampLoanDaysForLevel,
  daysFromLoanRow,
  formatDurationDays,
  graceDaysForLevel,
  graceEndIso,
  canRequestRollover,
  buildRolloverQuote,
  isCarryoverEligible,
  loanOutstanding,
  loanGraceDays,
  normalizeVerificationLevel,
  type BankAccount,
  type Borrower,
  type BorrowerTerms,
  type Installment,
  type KycStatus,
  type Loan,
  type LoanStatus,
  type NotificationRow,
  type RolloverQuote,
  type VerificationLevel,
} from "@/lib/types";

type Json = Record<string, unknown>;

function asNumber(v: unknown, fallback = 0): number {
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function parseTerms(row: Json | null | undefined, platformSchedule: ExpressRateBand[]): BorrowerTerms {
  const ratesRaw = (row?.custom_interest_rates ?? row?.rates) as Record<string, unknown> | null | undefined;
  const verifiedGuideRate = asNumber(
    row?.verified_guide_rate ??
      row?.verifiedGuideRate ??
      row?.sovereign_guide_rate ??
      row?.sovereignGuideRate ??
      ratesRaw?.["1m"],
    DEFAULT_TERMS.verifiedGuideRate,
  );
  const override = parseExpressRateSchedule(row?.express_rate_schedule);
  const schedule = override ?? platformSchedule;
  const legacyExpress = asNumber(
    row?.express_rate ?? row?.expressRate ?? row?.crest_rate ?? row?.crestRate,
    expressRateForDays(schedule, 7),
  );
  const expressRate = override ? expressRateForDays(schedule, 7) : legacyExpress;
  return {
    expressRateSchedule: schedule,
    expressRateScheduleOverride: override,
    expressRate,
    verifiedGuideRate,
    allowNegotiate: row?.allow_negotiate != null ? Boolean(row.allow_negotiate) : DEFAULT_TERMS.allowNegotiate,
    crestRate: expressRate,
    sovereignGuideRate: verifiedGuideRate,
  };
}

let cachedPlatformSchedule: ExpressRateBand[] | null = null;

export async function fetchPlatformExpressSchedule(): Promise<ExpressRateBand[]> {
  if (cachedPlatformSchedule) return cachedPlatformSchedule;
  const { data, error } = await supabase
    .from("platform_settings")
    .select("value")
    .eq("key", "express_rate_schedule")
    .maybeSingle();
  if (error || !data) {
    cachedPlatformSchedule = DEFAULT_EXPRESS_RATE_SCHEDULE;
    return cachedPlatformSchedule;
  }
  const parsed = parseExpressRateSchedule((data as Json).value);
  cachedPlatformSchedule = parsed ?? DEFAULT_EXPRESS_RATE_SCHEDULE;
  return cachedPlatformSchedule;
}

export async function savePlatformExpressSchedule(bands: ExpressRateBand[]): Promise<void> {
  const { error } = await supabase.from("platform_settings").upsert(
    {
      key: "express_rate_schedule",
      value: bands,
      updated_at: new Date().toISOString(),
    },
    { onConflict: "key" },
  );
  if (error) throw error;
  cachedPlatformSchedule = bands;
}

export function clearPlatformScheduleCache(): void {
  cachedPlatformSchedule = null;
}

function mapKycStatus(v: unknown): KycStatus {
  if (v === "pending" || v === "approved" || v === "declined") return v;
  return "not_started";
}

function mapVerificationLevel(v: unknown): VerificationLevel {
  return normalizeVerificationLevel(v);
}

function mapLoanStatus(v: unknown): LoanStatus {
  if (
    v === "pending" ||
    v === "approved" ||
    v === "countered" ||
    v === "counter_accepted" ||
    v === "active" ||
    v === "repaid" ||
    v === "overdue" ||
    v === "declined"
  ) {
    return v;
  }
  return "pending";
}

function mapInstallment(row: Json): Installment {
  const status = String(row.status ?? "pending");
  const paid = status === "paid" || Boolean(row.paid);
  return {
    id: String(row.id),
    loanId: String(row.loan_id),
    dueDate: String(row.due_date),
    amount: asNumber(row.amount_due ?? row.amount),
    paid,
    paidAt: row.paid_date ? String(row.paid_date) : row.paid_at ? String(row.paid_at) : undefined,
    status,
  };
}

function mapLoan(row: Json, installments: Installment[]): Loan {
  const durationDays =
    typeof row.duration_days === "number" && Number.isFinite(row.duration_days)
      ? Math.max(1, Math.round(row.duration_days))
      : daysFromLoanRow(row.duration, row.duration_days);
  const paid = installments.filter((i) => i.paid).reduce((s, i) => s + i.amount, 0);
  const dueDate = String(row.due_date);
  const verificationLevel = row.verification_level
    ? normalizeVerificationLevel(row.verification_level)
    : undefined;
  const graceDays =
    row.grace_days != null && Number.isFinite(Number(row.grace_days))
      ? Math.max(0, Math.round(Number(row.grace_days)))
      : verificationLevel
        ? graceDaysForLevel(verificationLevel)
        : undefined;
  return {
    id: String(row.id),
    borrowerId: String(row.borrower_id ?? row.user_id),
    amount: asNumber(row.amount_requested ?? row.amount),
    durationDays,
    interestRate: asNumber(row.interest_rate ?? row.interestRate),
    split: Boolean(row.split ?? row.is_split),
    status: mapLoanStatus(row.status),
    createdAt: String(row.created_at),
    dueDate,
    paid: asNumber(row.amount_paid, paid),
    installments,
    declineReason: row.decline_reason ? String(row.decline_reason) : undefined,
    proposedRepay:
      row.proposed_repay != null
        ? asNumber(row.proposed_repay)
        : row.total_repayable != null
          ? asNumber(row.total_repayable)
          : undefined,
    counterPrincipal: row.counter_principal != null ? asNumber(row.counter_principal) : undefined,
    counterRepay: row.counter_repay != null ? asNumber(row.counter_repay) : undefined,
    counterDurationDays: row.counter_duration_days != null ? asNumber(row.counter_duration_days) : undefined,
    counterDueDate: row.counter_due_date ? String(row.counter_due_date) : undefined,
    counterRate: row.counter_rate != null ? asNumber(row.counter_rate) : undefined,
    counterNote: row.counter_note ? String(row.counter_note) : undefined,
    verificationLevel: verificationLevel && verificationLevel !== "none" ? verificationLevel : undefined,
    graceDays,
    rolloverCount: row.rollover_count != null ? asNumber(row.rollover_count, 0) : 0,
    graceEndsAt: row.grace_ends_at
      ? String(row.grace_ends_at)
      : graceEndIso(dueDate, graceDays ?? GRACE_DAYS_FALLBACK),
  };
}

const GRACE_DAYS_FALLBACK = 2;

async function fetchInstallmentsForLoans(loanIds: string[]): Promise<Map<string, Installment[]>> {
  const map = new Map<string, Installment[]>();
  if (!loanIds.length) return map;
  const { data, error } = await supabase
    .from("repayment_schedule")
    .select("*")
    .in("loan_id", loanIds)
    .order("due_date", { ascending: true });
  if (error) throw error;
  for (const row of (data ?? []) as Json[]) {
    const inst = mapInstallment(row);
    const list = map.get(inst.loanId) ?? [];
    list.push(inst);
    map.set(inst.loanId, list);
  }
  return map;
}

export async function fetchBorrowerBundle(userId: string, profile: {
  full_name: string;
  email: string;
  phone: string | null;
  email_verified: boolean;
  created_at: string;
}, platformSchedule?: ExpressRateBand[]): Promise<Borrower> {
  const schedule = platformSchedule ?? (await fetchPlatformExpressSchedule());
  const [kycRes, settingsRes, unlockRes, scoreRes] = await Promise.all([
    supabase.from("kyc").select("*").eq("user_id", userId).order("created_at", { ascending: false }).limit(1).maybeSingle(),
    supabase.from("borrower_settings").select("*").eq("user_id", userId).maybeSingle(),
    supabase.from("credit_score_unlocks").select("id").eq("user_id", userId).limit(1).maybeSingle(),
    supabase.from("credit_scores").select("*").eq("user_id", userId).order("calculated_at", { ascending: false }).limit(1).maybeSingle(),
  ]);

  if (kycRes.error) throw kycRes.error;
  if (settingsRes.error) throw settingsRes.error;
  if (unlockRes.error) throw unlockRes.error;
  if (scoreRes.error) throw scoreRes.error;

  const kyc = kycRes.data as Json | null;
  const settings = settingsRes.data as Json | null;
  const terms = parseTerms(settings, schedule);
  const loanLimit = asNumber(settings?.loan_limit ?? settings?.limit, 0);
  const score = asNumber(scoreRes.data?.score, 0);

  let docs: Json[] = [];
  if (kyc?.id) {
    const { data } = await supabase.from("kyc_documents").select("*").eq("kyc_id", kyc.id);
    docs = (data ?? []) as Json[];
  }
  const docName = (type: string) => {
    const d = docs.find((x) => String(x.document_type ?? x.type) === type);
    return d ? String(d.file_name ?? d.storage_path ?? type) : undefined;
  };
  const documents = docs.map((d) => ({
    type: String(d.document_type ?? d.type ?? ""),
    fileName: String(d.file_name ?? d.storage_path ?? ""),
    storagePath: String(d.storage_path ?? ""),
  })).filter((d) => d.type && d.storagePath);

  const levelFromKyc = mapVerificationLevel(kyc?.verification_level);
  const levelFromSettings = mapVerificationLevel(settings?.verification_level);
  const verificationLevel: VerificationLevel =
    levelFromSettings !== "none"
      ? levelFromSettings
      : levelFromKyc !== "none"
        ? levelFromKyc
        : kyc && mapKycStatus(kyc.status) === "approved"
          ? "express"
          : "none";

  return {
    id: userId,
    name: profile.full_name,
    email: profile.email,
    phone: profile.phone ?? "",
    createdAt: profile.created_at,
    emailVerified: profile.email_verified,
    kycStatus: kyc ? mapKycStatus(kyc.status) : "not_started",
    verificationLevel,
    kycId: kyc?.id ? String(kyc.id) : undefined,
    kyc: kyc
      ? {
          bvn: kyc.bvn ? String(kyc.bvn) : undefined,
          nin: kyc.nin ? String(kyc.nin) : undefined,
          dateOfBirth: kyc.date_of_birth ? String(kyc.date_of_birth) : undefined,
          houseAddress: kyc.house_address ? String(kyc.house_address) : undefined,
          officeAddress: kyc.office_address ? String(kyc.office_address) : undefined,
          workplaceName: kyc.workplace_name ? String(kyc.workplace_name) : undefined,
          salaryAmount: kyc.salary_amount != null ? asNumber(kyc.salary_amount) : undefined,
          loanPurpose: kyc.loan_purpose ? String(kyc.loan_purpose) : undefined,
          repaymentPlan: kyc.repayment_plan ? String(kyc.repayment_plan) : undefined,
          socialInstagram: kyc.social_instagram ? String(kyc.social_instagram) : undefined,
          socialFacebook: kyc.social_facebook ? String(kyc.social_facebook) : undefined,
          socialTwitter: kyc.social_twitter ? String(kyc.social_twitter) : undefined,
          socialYoutube: kyc.social_youtube ? String(kyc.social_youtube) : undefined,
          nokName: kyc.nok_name ? String(kyc.nok_name) : undefined,
          nokRelationship: kyc.nok_relationship ? String(kyc.nok_relationship) : undefined,
          nokPhone: kyc.nok_phone ? String(kyc.nok_phone) : undefined,
          nokAddress: kyc.nok_address ? String(kyc.nok_address) : undefined,
          guarantorName: kyc.guarantor_name ? String(kyc.guarantor_name) : undefined,
          guarantorRelationship: kyc.guarantor_relationship ? String(kyc.guarantor_relationship) : undefined,
          guarantorPhone: kyc.guarantor_phone ? String(kyc.guarantor_phone) : undefined,
          guarantorAddress: kyc.guarantor_address ? String(kyc.guarantor_address) : undefined,
          consentGiven: Boolean(kyc.consent_given),
          consentGivenAt: kyc.consent_given_at ? String(kyc.consent_given_at) : undefined,
          verificationLevel: levelFromKyc !== "none" ? levelFromKyc : undefined,
          idFrontName: docName("id_front") ?? docName("nin_front"),
          idBackName: docName("id_back"),
          selfieName: docName("selfie"),
          statementName: docName("bank_statement"),
          documents,
          aiSummary: kyc.ai_analysis_summary ? String(kyc.ai_analysis_summary) : undefined,
          aiSuggestedLimit: kyc.ai_suggested_limit != null ? asNumber(kyc.ai_suggested_limit) : undefined,
          aiRiskScore: kyc.ai_risk_score != null ? asNumber(kyc.ai_risk_score) : undefined,
          aiAnalysisRaw: kyc.ai_analysis_raw,
          aiKycContextSummary: kyc.ai_kyc_context_summary ? String(kyc.ai_kyc_context_summary) : undefined,
          aiKycContextRaw: kyc.ai_kyc_context_raw,
          declineReason: kyc.decline_reason ? String(kyc.decline_reason) : undefined,
          submittedAt: kyc.submitted_at
            ? String(kyc.submitted_at)
            : kyc.created_at
              ? String(kyc.created_at)
              : undefined,
        }
      : undefined,
    loanLimit,
    creditScore: score,
    creditScoreUnlocked: Boolean(unlockRes.data),
    terms,
  };
}

export async function fetchLoansForBorrower(borrowerId: string): Promise<Loan[]> {
  const { data, error } = await supabase
    .from("loans")
    .select("*")
    .eq("borrower_id", borrowerId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  const rows = (data ?? []) as Json[];
  const map = await fetchInstallmentsForLoans(rows.map((r) => String(r.id)));
  return rows.map((r) => mapLoan(r, map.get(String(r.id)) ?? []));
}

export async function fetchAllLoans(): Promise<Loan[]> {
  const { data, error } = await supabase.from("loans").select("*").order("created_at", { ascending: false });
  if (error) throw error;
  const rows = (data ?? []) as Json[];
  const map = await fetchInstallmentsForLoans(rows.map((r) => String(r.id)));
  return rows.map((r) => mapLoan(r, map.get(String(r.id)) ?? []));
}

export async function fetchLoanById(loanId: string): Promise<Loan | null> {
  const { data, error } = await supabase.from("loans").select("*").eq("id", loanId).maybeSingle();
  if (error) throw error;
  if (!data) return null;
  const map = await fetchInstallmentsForLoans([loanId]);
  return mapLoan(data as Json, map.get(loanId) ?? []);
}

export async function fetchAllBorrowers(): Promise<Borrower[]> {
  const { data, error } = await supabase
    .from("profiles")
    .select("*")
    .eq("role", "borrower")
    .order("created_at", { ascending: false });
  if (error) throw error;
  const profiles = data ?? [];
  const platformSchedule = await fetchPlatformExpressSchedule();
  return Promise.all(
    profiles.map((p) =>
      fetchBorrowerBundle(p.id, {
        full_name: p.full_name,
        email: p.email,
        phone: p.phone,
        email_verified: p.email_verified,
        created_at: p.created_at,
      }, platformSchedule),
    ),
  );
}

export async function fetchPrimaryBankAccount(userId: string): Promise<BankAccount | null> {
  const { data, error } = await supabase
    .from("bank_accounts")
    .select("*")
    .eq("user_id", userId)
    .order("is_primary", { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  const row = data as Json;
  return {
    id: String(row.id),
    bankName: String(row.bank_name ?? ""),
    accountNumber: String(row.account_number ?? ""),
    accountName: String(row.account_name ?? ""),
    bankCode: String(row.bank_code ?? ""),
    isPrimary: Boolean(row.is_primary),
    verified: Boolean(row.verified),
  };
}

/** Persist disbursement bank locally, then register Flutterwave recipient. */
export async function saveDisbursementBankAccount(
  input: {
    userId: string;
    bankName: string;
    bankCode: string;
    accountNumber: string;
    accountName: string;
  },
  opts?: { requireRecipient?: boolean },
): Promise<BankAccount> {
  const accountNumber = input.accountNumber.replace(/\D/g, "");
  if (!input.bankCode) throw new Error("Select a bank.");
  if (accountNumber.length < 10) throw new Error("Enter a valid 10-digit account number.");
  if (!input.accountName.trim()) throw new Error("Verify the account name before saving.");

  const existing = await fetchPrimaryBankAccount(input.userId);
  if (existing) {
    const { error } = await supabase
      .from("bank_accounts")
      .update({
        bank_name: input.bankName,
        bank_code: input.bankCode,
        account_number: accountNumber,
        account_name: input.accountName.trim(),
        is_primary: true,
        verified: true,
      })
      .eq("id", existing.id);
    if (error) throw error;
  } else {
    const { error } = await supabase.from("bank_accounts").insert({
      user_id: input.userId,
      bank_name: input.bankName,
      bank_code: input.bankCode,
      account_number: accountNumber,
      account_name: input.accountName.trim(),
      is_primary: true,
      verified: true,
    });
    if (error) throw error;
  }

  try {
    await invokeFunction("create-flutterwave-recipient", {
      account_number: accountNumber,
      bank_code: input.bankCode,
      account_name: input.accountName.trim(),
      bank_name: input.bankName,
    });
  } catch (e) {
    if (opts?.requireRecipient !== false) throw e;
    console.error("create-flutterwave-recipient", e);
  }

  const row = await fetchPrimaryBankAccount(input.userId);
  if (!row) throw new Error("Bank account could not be saved.");
  // Security alert when bank details change (or are first saved).
  sendActivityEmail("bank_account_changed", input.userId);
  return row;
}

export async function fetchNotifications(userId: string): Promise<NotificationRow[]> {
  const { data, error } = await supabase
    .from("notifications")
    .select("*")
    .eq("user_id", userId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return ((data ?? []) as Json[]).map((n) => ({
    id: String(n.id),
    userId: String(n.user_id),
    body: String(n.body ?? n.message ?? n.title ?? ""),
    createdAt: String(n.created_at),
    read: Boolean(n.is_read ?? n.read),
  }));
}

export async function markNotificationRead(id: string): Promise<void> {
  const { error } = await supabase
    .from("notifications")
    .update({ is_read: true, read_at: new Date().toISOString() })
    .eq("id", id);
  if (error) throw error;
}

export async function markAllNotificationsRead(userId: string): Promise<void> {
  const { error } = await supabase
    .from("notifications")
    .update({ is_read: true, read_at: new Date().toISOString() })
    .eq("user_id", userId)
    .eq("is_read", false);
  if (error) throw error;
}

export async function insertNotification(
  userId: string,
  body: string,
  options?: { title?: string; type?: string },
): Promise<void> {
  const title = (options?.title ?? "CrediKing").trim() || "CrediKing";
  // Must match public.notification_type enum — see supabase/sql/notifications_type_enum.sql
  const type = (options?.type ?? "system").trim() || "system";
  const { error } = await supabase.from("notifications").insert({
    user_id: userId,
    title,
    body,
    message: body,
    type,
    is_read: false,
  });
  if (error) throw error;
}

export type UploadedKycDoc = {
  type: string;
  path: string;
  name: string;
};

/** Buffer file then upload so picks don't go stale (ERR_UPLOAD_FILE_CHANGED). */
export async function uploadKycDocNow(
  userId: string,
  file: File,
  docType: string,
): Promise<UploadedKycDoc> {
  const buf = await file.arrayBuffer();
  const blob = new Blob([buf], { type: file.type || "application/octet-stream" });
  const ext = file.name.split(".").pop() || "bin";
  const path = `${userId}/${docType}-${Date.now()}.${ext}`;
  const { error } = await supabase.storage.from("kyc-documents").upload(path, blob, {
    upsert: true,
    contentType: file.type || "application/octet-stream",
  });
  if (error) throw error;

  // Confirm the object is actually readable (catches silent / RLS storage failures)
  const exists = await kycStoragePathExists(path);
  if (!exists) {
    throw new Error(
      "Upload did not land in storage. Check that the kyc-documents bucket and upload policies are set.",
    );
  }
  return { type: docType, path, name: file.name };
}

/** True if a path in the kyc-documents bucket can be signed (i.e. object exists & readable). */
export async function kycStoragePathExists(storagePath: string): Promise<boolean> {
  if (!storagePath) return false;
  const { data, error } = await supabase.storage
    .from("kyc-documents")
    .createSignedUrl(storagePath, 60);
  return !error && Boolean(data?.signedUrl);
}

/** Keep only draft docs whose storage objects still exist. */
export async function filterExistingKycDocs(
  docs: Partial<Record<string, UploadedKycDoc>>,
): Promise<Partial<Record<string, UploadedKycDoc>>> {
  const entries = Object.entries(docs).filter(([, d]) => d?.path);
  const checked = await Promise.all(
    entries.map(async ([type, doc]) => {
      const ok = await kycStoragePathExists(doc!.path);
      return ok ? ([type, doc!] as const) : null;
    }),
  );
  const out: Partial<Record<string, UploadedKycDoc>> = {};
  for (const row of checked) {
    if (row) out[row[0]] = row[1];
  }
  return out;
}

export type SubmitKycInput = {
  userId: string;
  verificationLevel: "express" | "verified";
  bvn: string;
  nin: string;
  dateOfBirth: string;
  houseAddress: string;
  officeAddress: string;
  workplaceName: string;
  salaryAmount: number;
  loanPurpose: string;
  repaymentPlan: string;
  socialInstagram: string;
  socialFacebook: string;
  socialTwitter: string;
  socialYoutube: string;
  nokName: string;
  nokRelationship: string;
  nokPhone: string;
  nokAddress: string;
  guarantorName: string;
  guarantorRelationship: string;
  guarantorPhone: string;
  guarantorAddress: string;
  /** Already uploaded to storage */
  documents: UploadedKycDoc[];
  bankCode: string;
  bankName: string;
  accountNumber: string;
  accountName: string;
};

const EXPRESS_REQUIRED_DOCS = [...EXPRESS_DOC_TYPES] as const;
const VERIFIED_REQUIRED_DOCS = [
  ...EXPRESS_DOC_TYPES,
  ...EXPRESS_OPTIONAL_DOC_TYPES,
  ...VERIFIED_EXTRA_DOC_TYPES,
] as const;

function uniqueIdConflictMessage(err: { code?: string; message?: string }): string | null {
  if (err.code !== "23505") return null;
  const msg = (err.message ?? "").toLowerCase();
  if (msg.includes("bvn")) {
    return "This BVN is already registered on another account. Contact support if you believe this is an error.";
  }
  if (msg.includes("nin")) {
    return "This NIN is already registered on another account. Contact support if you believe this is an error.";
  }
  return "This BVN/NIN is already registered on the platform. Contact support if you believe this is an error.";
}

/**
 * Returns true if BVN/NIN is already used by a different user.
 * Prefers SECURITY DEFINER RPC (see supabase/sql/is_kyc_identifier_taken.sql);
 * falls back to a direct select when the RPC is missing.
 */
export async function isKycIdentifierTaken(
  field: "bvn" | "nin",
  value: string,
  userId: string,
): Promise<boolean> {
  const digits = value.replace(/\D/g, "");
  if (digits.length !== 11) return false;

  const { data: rpcData, error: rpcErr } = await supabase.rpc("is_kyc_identifier_taken", {
    p_field: field,
    p_value: digits,
  });
  if (!rpcErr && typeof rpcData === "boolean") return rpcData;

  const { data, error } = await supabase
    .from("kyc")
    .select("id, user_id")
    .eq(field, digits)
    .limit(1)
    .maybeSingle();
  // 0 rows = available (some clients surface this as PGRST116)
  if (error && error.code !== "PGRST116") throw error;
  if (!data) return false;
  return String((data as Json).user_id) !== userId;
}

async function assertBvnNinAvailable(userId: string, bvn: string, nin: string) {
  if (!bvn || bvn.length !== 11) {
    throw new Error("A valid 11-digit BVN is required.");
  }
  if (!nin || nin.length !== 11) {
    throw new Error("A valid 11-digit NIN is required.");
  }
  if (await isKycIdentifierTaken("bvn", bvn, userId)) {
    throw new Error(
      "This BVN is already registered on another account. Contact support if you believe this is an error.",
    );
  }
  if (await isKycIdentifierTaken("nin", nin, userId)) {
    throw new Error(
      "This NIN is already registered on another account. Contact support if you believe this is an error.",
    );
  }
}

export async function submitKyc(input: SubmitKycInput): Promise<{ kycId: string }> {
  const now = new Date().toISOString();
  const bvn = input.bvn.replace(/\D/g, "") || "";
  const nin = input.nin.replace(/\D/g, "") || "";
  const byType = new Map(input.documents.map((d) => [d.type, d]));
  const requiredDocs = [
    ...(input.verificationLevel === "verified" ? VERIFIED_REQUIRED_DOCS : EXPRESS_REQUIRED_DOCS),
  ];
  for (const t of requiredDocs) {
    if (!byType.get(t)?.path) {
      throw new Error(`Missing uploaded document: ${t.replace(/_/g, " ")}. Please re-select it.`);
    }
  }
  const statement = byType.get("bank_statement");

  await assertBvnNinAvailable(input.userId, bvn, nin);

  for (const t of requiredDocs) {
    const doc = byType.get(t)!;
    const ok = await kycStoragePathExists(doc.path);
    if (!ok) {
      throw new Error(
        `${t.replace(/_/g, " ")} is missing from storage. Please re-upload it on the Documents step.`,
      );
    }
  }

  const { data: existingRow, error: existingErr } = await supabase
    .from("kyc")
    .select("id, status, verification_level")
    .eq("user_id", input.userId)
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  if (existingErr && existingErr.code !== "PGRST116") throw existingErr;

  const existing = existingRow as Json | null;
  const existingStatus = existing ? String(existing.status) : "";
  const existingLevel = mapVerificationLevel(existing?.verification_level);
  const isVerifiedUpgradeSubmit =
    existingStatus === "approved" &&
    existingLevel === "express" &&
    input.verificationLevel === "verified";

  if (existingStatus === "pending") {
    throw new Error(
      "Your verification is already under review. Please wait for a decision before submitting again.",
    );
  }
  if (existingStatus === "approved" && !isVerifiedUpgradeSubmit) {
    throw new Error("Your KYC is already approved. You can request a loan from the application page.");
  }

  const fields = {
    status: "pending" as const,
    verification_level: input.verificationLevel,
    bvn: bvn || null,
    nin: nin || null,
    date_of_birth: input.dateOfBirth || null,
    house_address: input.houseAddress || null,
    office_address: input.officeAddress || null,
    workplace_name: input.workplaceName || null,
    salary_amount: input.salaryAmount || null,
    loan_purpose: input.loanPurpose || null,
    repayment_plan: input.repaymentPlan || null,
    social_instagram: input.socialInstagram || null,
    social_facebook: input.socialFacebook || null,
    social_twitter: input.socialTwitter || null,
    social_youtube: input.socialYoutube || null,
    nok_name: input.nokName || null,
    nok_relationship: input.nokRelationship || null,
    nok_phone: input.nokPhone || null,
    nok_address: input.nokAddress || null,
    guarantor_name: input.guarantorName || null,
    guarantor_relationship: input.guarantorRelationship || null,
    guarantor_phone: input.guarantorPhone || null,
    guarantor_address: input.guarantorAddress || null,
    consent_given: true,
    consent_given_at: now,
    submitted_at: now,
    decline_reason: null,
  };

  let kycId: string;
  if (existing?.id && isVerifiedUpgradeSubmit) {
    // RLS often blocks UPDATE on approved KYC — use SECURITY DEFINER RPC.
    const { data: upgradedId, error: rpcErr } = await supabase.rpc("request_verified_kyc_upgrade", {
      p_fields: fields,
    });
    if (rpcErr) {
      const friendly = uniqueIdConflictMessage(rpcErr);
      if (friendly) throw new Error(friendly);
      const msg = (rpcErr.message ?? "").toLowerCase();
      if (msg.includes("could not find the function") || msg.includes("schema cache")) {
        throw new Error(
          "Verified upgrade is not enabled on the server yet. Ask support to run kyc_verified_upgrade.sql.",
        );
      }
      throw new Error(rpcErr.message || "Could not submit your Verified upgrade.");
    }
    if (!upgradedId) {
      throw new Error("Could not submit your Verified upgrade. Please try again or contact support.");
    }
    kycId = String(upgradedId);

    const { error: delErr } = await supabase.from("kyc_documents").delete().eq("kyc_id", kycId);
    if (delErr) throw delErr;
  } else if (existing?.id) {
    kycId = String(existing.id);
    const { data: updated, error: kycErr } = await supabase
      .from("kyc")
      .update(fields)
      .eq("id", kycId)
      .select("id, status")
      .maybeSingle();
    if (kycErr) {
      const friendly = uniqueIdConflictMessage(kycErr);
      if (friendly) throw new Error(friendly);
      throw kycErr;
    }
    if (!updated || String((updated as Json).status) !== "pending") {
      throw new Error(
        "Could not submit your verification for review. Please try again or contact support.",
      );
    }

    const { error: delErr } = await supabase.from("kyc_documents").delete().eq("kyc_id", kycId);
    if (delErr) throw delErr;
  } else {
    const { data: kyc, error: kycErr } = await supabase
      .from("kyc")
      .insert({
        user_id: input.userId,
        ...fields,
      })
      .select("id, status")
      .maybeSingle();
    if (kycErr && kycErr.code !== "PGRST116") {
      const friendly = uniqueIdConflictMessage(kycErr);
      if (friendly) throw new Error(friendly);
      throw kycErr;
    }
    if (kyc?.id) {
      kycId = String((kyc as Json).id);
      if (String((kyc as Json).status) !== "pending") {
        throw new Error(
          "Could not submit your verification for review. Please try again or contact support.",
        );
      }
    } else {
      const { data: refetch, error: refetchErr } = await supabase
        .from("kyc")
        .select("id, status")
        .eq("user_id", input.userId)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (refetchErr && refetchErr.code !== "PGRST116") throw refetchErr;
      if (!refetch?.id || String((refetch as Json).status) !== "pending") {
        throw new Error("KYC was saved but could not be confirmed. Please refresh and try again.");
      }
      kycId = String((refetch as Json).id);
    }
  }

  const { error: docsErr } = await supabase.from("kyc_documents").insert(
    input.documents.map((d) => ({
      kyc_id: kycId,
      user_id: input.userId,
      document_type: d.type,
      storage_path: d.path,
      file_url: d.path,
      file_name: d.name,
    })),
  );
  if (docsErr) throw docsErr;

  await saveDisbursementBankAccount(
    {
      userId: input.userId,
      bankName: input.bankName,
      bankCode: input.bankCode,
      accountNumber: input.accountNumber,
      accountName: input.accountName,
    },
    { requireRecipient: false },
  );

  void (async () => {
    try {
      if (statement?.path) {
        await invokeFunction("analyze-bank-statement", {
          kyc_id: kycId,
          storage_path: statement.path,
        });
      }
      await invokeFunction("analyze-kyc-context", { kyc_id: kycId });
    } catch (e) {
      console.error("KYC AI background", e);
    }
  })();

  const tierLabel = TIER_LABEL[input.verificationLevel];
  await insertNotification(
    input.userId,
    `Your ${tierLabel} verification was submitted and is under review.`,
    { title: "KYC submitted", type: "kyc" },
  );
  sendActivityEmail("kyc_received", input.userId, { tier: tierLabel });

  return { kycId };
}

/**
 * Download a locked bank-statement PDF, unlock it in-browser with the given password,
 * re-upload, and point the KYC document row at the unlocked file.
 * Password is never persisted.
 */
export async function unlockAndReplaceBankStatement(input: {
  userId: string;
  kycId: string;
  storagePath: string;
  password: string;
}): Promise<{ path: string }> {
  const { unlockPdfFileIfNeeded } = await import("@/lib/pdf-unlock");
  const signed = await createKycDocSignedUrl(input.storagePath, 120);
  if (!signed.url) throw new Error(signed.error || "Could not download bank statement.");

  if (!input.password.trim()) {
    throw new Error("Enter the PDF password to unlock this statement.");
  }

  const res = await fetch(signed.url);
  if (!res.ok) throw new Error(`Could not download bank statement (${res.status}).`);
  const blob = await res.arrayBuffer();
  const locked = new File([blob], "bank-statement.pdf", { type: "application/pdf" });
  const unlocked = await unlockPdfFileIfNeeded(locked, input.password);

  // Already readable — no need to replace storage object.
  if (unlocked === locked) {
    return { path: input.storagePath };
  }

  const uploaded = await uploadKycDocNow(input.userId, unlocked, "bank_statement");
  const { error } = await supabase
    .from("kyc_documents")
    .update({
      storage_path: uploaded.path,
      file_url: uploaded.path,
      file_name: uploaded.name,
    })
    .eq("kyc_id", input.kycId)
    .eq("document_type", "bank_statement");
  if (error) throw error;
  return { path: uploaded.path };
}

export async function createKycDocSignedUrl(
  storagePath: string,
  expiresIn = 3600,
): Promise<{ url: string | null; error?: string }> {
  if (!storagePath) return { url: null, error: "Missing storage path" };
  const { data, error } = await supabase.storage
    .from("kyc-documents")
    .createSignedUrl(storagePath, expiresIn);
  if (error) return { url: null, error: error.message };
  return { url: data?.signedUrl ?? null };
}

export async function createLoanApplication(input: {
  borrowerId: string;
  amount: number;
  durationDays: number;
  verificationLevel: VerificationLevel;
  proposedRepay?: number;
  expressRateSchedule?: ExpressRateBand[];
}): Promise<Loan> {
  const level = input.verificationLevel;
  const days = clampLoanDaysForLevel(level, input.durationDays);
  const dueDate = new Date(Date.now() + days * 86400000).toISOString();

  let interestRate: number;
  let totalRepayable: number;
  let proposedRepay: number | null = null;

  if (level === "express") {
    const schedule = input.expressRateSchedule ?? DEFAULT_EXPRESS_RATE_SCHEDULE;
    interestRate = expressRateForDays(schedule, days);
    totalRepayable = expressRepayAmount(input.amount, schedule, days);
    proposedRepay = totalRepayable;
  } else {
    const repay = input.proposedRepay;
    if (repay == null || !Number.isFinite(repay) || repay < input.amount) {
      throw new Error("Enter how much you would like to pay back (must be at least the amount borrowed).");
    }
    proposedRepay = repay;
    interestRate = impliedRatePercent(input.amount, repay);
    totalRepayable = repay;
  }

  const graceDays = graceDaysForLevel(level);

  const { data, error } = await supabase
    .from("loans")
    .insert({
      borrower_id: input.borrowerId,
      amount_requested: input.amount,
      duration: `${days}d`,
      duration_days: days,
      interest_rate: interestRate,
      split: false,
      status: "pending",
      due_date: dueDate,
      total_repayable: totalRepayable,
      proposed_repay: proposedRepay,
      verification_level: level,
      grace_days: graceDays,
      rollover_count: 0,
      grace_ends_at: graceEndIso(dueDate, graceDays),
    })
    .select("*")
    .single();
  if (error) throw error;
  const loan = mapLoan(data as Json, []);
  sendActivityEmail("loan_application_received", input.borrowerId, {
    loan_ref: loan.id.slice(0, 8).toUpperCase(),
    amount: input.amount,
  });
  return loan;
}

export async function declineLoan(loanId: string, reason: string, borrowerId: string): Promise<void> {
  const { error } = await supabase
    .from("loans")
    .update({ status: "declined", decline_reason: reason })
    .eq("id", loanId);
  if (error) throw error;
  await insertNotification(borrowerId, `Your loan was declined: ${reason}`, {
    title: "Loan declined",
    type: "loan",
  });
  sendActivityEmail("loan_declined", borrowerId, {
    loan_ref: loanId.slice(0, 8).toUpperCase(),
    reason,
  });
}

/** Marks loan approved, then triggers Flutterwave disbursement (pending → approved → active). */
export async function approveAndDisburseLoan(loanId: string, borrowerId: string) {
  const bank = await fetchPrimaryBankAccount(borrowerId);
  if (!bank) {
    throw new Error(
      "Borrower has no bank account on file. Ask them to add a disbursement account on their dashboard, then retry.",
    );
  }

  const { data: loan, error: fetchErr } = await supabase
    .from("loans")
    .select("id, status")
    .eq("id", loanId)
    .maybeSingle();
  if (fetchErr) throw fetchErr;
  if (!loan) throw new Error("Loan not found.");

  const status = String((loan as Json).status ?? "");
  if (status === "pending" || status === "counter_accepted") {
    const { error } = await supabase.from("loans").update({ status: "approved" }).eq("id", loanId);
    if (error) throw error;
    await insertNotification(borrowerId, "Your loan was approved. Disbursement is being processed.", {
      title: "Loan approved",
      type: "loan",
    });
    // Fire-and-forget approval email (details + policy links). Do not block disbursement.
    void invokeFunction("send-loan-approval-email", { loan_id: loanId }).catch((e) => {
      console.error("send-loan-approval-email", e);
    });
  } else if (status !== "approved") {
    throw new Error(`Loan cannot be disbursed from status "${status}".`);
  }

  const result = await invokeFunction<{ amount?: number; due_date?: string }>("disburse-loan", {
    loan_id: loanId,
  });
  // Fetch loan for amount/due if disburse response is thin
  const { data: funded } = await supabase
    .from("loans")
    .select("amount_requested, amount, due_date")
    .eq("id", loanId)
    .maybeSingle();
  const fundedRow = funded as Json | null;
  sendActivityEmail("loan_disbursed", borrowerId, {
    loan_ref: loanId.slice(0, 8).toUpperCase(),
    amount: result?.amount ?? asNumber(fundedRow?.amount_requested ?? fundedRow?.amount),
    due_date: fundedRow?.due_date
      ? new Date(String(fundedRow.due_date)).toLocaleDateString("en-NG")
      : undefined,
  });
  return result;
}

export async function markInstallmentPaid(input: {
  installmentId: string;
  loanId: string;
  borrowerId: string;
  adminId: string;
}): Promise<void> {
  const { error } = await supabase
    .from("repayment_schedule")
    .update({
      status: "paid",
      paid_date: new Date().toISOString(),
      payment_method: "manual",
      marked_by: input.adminId,
    })
    .eq("id", input.installmentId);
  if (error) throw error;

  const { data: rows, error: listErr } = await supabase
    .from("repayment_schedule")
    .select("status")
    .eq("loan_id", input.loanId);
  if (listErr) throw listErr;
  const allPaid = (rows ?? []).every((r) => String((r as Json).status) === "paid");
  if (allPaid) {
    const { error: loanErr } = await supabase.from("loans").update({ status: "repaid" }).eq("id", input.loanId);
    if (loanErr) throw loanErr;
  }
  await insertNotification(
    input.borrowerId,
    `An installment on loan ${input.loanId.slice(0, 8).toUpperCase()} was marked as paid.`,
    { title: "Payment recorded", type: "payment" },
  );
}

export async function approveKyc(userId: string, kycId: string, level?: "express" | "verified"): Promise<void> {
  const { data: kycRow } = await supabase
    .from("kyc")
    .select("verification_level")
    .eq("id", kycId)
    .maybeSingle();
  const tier = level ?? mapVerificationLevel((kycRow as Json | null)?.verification_level);
  const resolved: "express" | "verified" = tier === "verified" || tier === "elite" ? "verified" : "express";

  const { error } = await supabase
    .from("kyc")
    .update({ status: "approved", verification_level: resolved })
    .eq("id", kycId);
  if (error) throw error;

  await supabase.from("borrower_settings").upsert(
    {
      user_id: userId,
      verification_level: resolved,
      crest_rate: EXPRESS_FIXED_RATE,
    },
    { onConflict: "user_id" },
  );

  await insertNotification(
    userId,
    `Your ${TIER_LABEL[resolved]} verification was approved.`,
    { title: "KYC approved", type: "kyc" },
  );
  sendActivityEmail("kyc_approved", userId, { tier: TIER_LABEL[resolved] });
}

export async function grantElite(userId: string): Promise<void> {
  const { data: settings } = await supabase
    .from("borrower_settings")
    .select("loan_limit, verification_level")
    .eq("user_id", userId)
    .maybeSingle();
  const current = mapVerificationLevel((settings as Json | null)?.verification_level);
  if (current !== "verified" && current !== "elite") {
    throw new Error("Elite can only be granted to Verified members.");
  }
  const limit = asNumber((settings as Json | null)?.loan_limit, 0);
  const payload: Json = {
    user_id: userId,
    verification_level: "elite",
  };
  if (limit < ELITE_DEFAULT_LIMIT) {
    payload.loan_limit = ELITE_DEFAULT_LIMIT;
  }
  const { error } = await supabase.from("borrower_settings").upsert(payload, { onConflict: "user_id" });
  if (error) throw error;
  await insertNotification(userId, "You've been granted Elite membership. Higher limits and flexible terms are now available.", {
    title: "Elite granted",
    type: "kyc",
  });
  sendActivityEmail("tier_upgrade", userId, { tier: TIER_LABEL.elite });
}

/**
 * Set or demote membership. Use `none` to unapprove (revokes borrowing until re-approved).
 * Does not delete KYC documents.
 */
export async function setBorrowerMembership(
  userId: string,
  level: VerificationLevel,
  opts?: { reason?: string; kycId?: string },
): Promise<void> {
  const { data: settings } = await supabase
    .from("borrower_settings")
    .select("loan_limit, verification_level")
    .eq("user_id", userId)
    .maybeSingle();
  const currentLimit = asNumber((settings as Json | null)?.loan_limit, 0);

  const payload: Json = {
    user_id: userId,
    verification_level: level,
  };
  if (level === "express" && currentLimit <= 0) payload.loan_limit = EXPRESS_DEFAULT_LIMIT;
  if (level === "verified" && currentLimit < VERIFIED_DEFAULT_LIMIT) payload.loan_limit = VERIFIED_DEFAULT_LIMIT;
  if (level === "elite" && currentLimit < ELITE_DEFAULT_LIMIT) payload.loan_limit = ELITE_DEFAULT_LIMIT;

  const { error } = await supabase.from("borrower_settings").upsert(payload, { onConflict: "user_id" });
  if (error) throw error;

  if (level === "none") {
    if (opts?.kycId) {
      const { error: kycErr } = await supabase
        .from("kyc")
        .update({
          status: "declined",
          decline_reason: opts.reason?.trim() || "Membership revoked by admin",
          verification_level: "none",
        })
        .eq("id", opts.kycId);
      if (kycErr) throw kycErr;
    } else {
      await supabase
        .from("kyc")
        .update({
          status: "declined",
          decline_reason: opts?.reason?.trim() || "Membership revoked by admin",
          verification_level: "none",
        })
        .eq("user_id", userId)
        .eq("status", "approved");
    }
    await insertNotification(
      userId,
      opts?.reason?.trim()
        ? `Your membership was revoked: ${opts.reason.trim()}`
        : "Your membership was revoked. Contact support if you have questions.",
      { title: "Membership revoked", type: "kyc" },
    );
    return;
  }

  if (opts?.kycId) {
    await supabase
      .from("kyc")
      .update({
        status: "approved",
        verification_level: level === "elite" ? "verified" : level,
        decline_reason: null,
      })
      .eq("id", opts.kycId);
  }

  const label = level === "elite" ? TIER_LABEL.elite : TIER_LABEL[level];
  await insertNotification(
    userId,
    `Your membership is now ${label}.`,
    { title: "Membership updated", type: "kyc" },
  );
  sendActivityEmail("tier_upgrade", userId, { tier: label });
}

export async function declineKyc(userId: string, kycId: string, reason: string): Promise<void> {
  const declineReason = reason || "Not approved";

  const [{ data: kycRow }, { data: settings }] = await Promise.all([
    supabase.from("kyc").select("verification_level, status").eq("id", kycId).maybeSingle(),
    supabase.from("borrower_settings").select("verification_level").eq("user_id", userId).maybeSingle(),
  ]);

  const requested = mapVerificationLevel((kycRow as Json | null)?.verification_level);
  const membership = mapVerificationLevel((settings as Json | null)?.verification_level);
  const isUpgradeDecline =
    String((kycRow as Json | null)?.status) === "pending" &&
    (requested === "verified" || requested === "elite") &&
    membership === "express";

  if (isUpgradeDecline) {
    // Keep Express access; clear the Verified request so they can fix docs and upgrade again.
    const { error } = await supabase
      .from("kyc")
      .update({
        status: "approved",
        verification_level: "express",
        decline_reason: declineReason,
      })
      .eq("id", kycId);
    if (error) throw error;
    await insertNotification(
      userId,
      `Your Verified upgrade was declined${reason ? `: ${reason}` : "."} You remain on Express and can re-apply after fixing the issues.`,
      { title: "Verified upgrade declined", type: "kyc" },
    );
    sendActivityEmail("kyc_rejected", userId, {
      reason: reason || "Verified upgrade declined",
      tier: TIER_LABEL.verified,
    });
    return;
  }

  const { error } = await supabase
    .from("kyc")
    .update({ status: "declined", decline_reason: declineReason })
    .eq("id", kycId);
  if (error) throw error;
  await insertNotification(userId, `Your KYC was declined${reason ? `: ${reason}` : "."}`, {
    title: "KYC declined",
    type: "kyc",
  });
  sendActivityEmail("kyc_rejected", userId, { reason: declineReason });
}

/** Admin: correct borrower display name / phone on profiles. */
export async function updateBorrowerProfile(
  userId: string,
  patch: { fullName?: string; phone?: string },
): Promise<void> {
  const payload: Json = {};
  if (patch.fullName != null) {
    const name = patch.fullName.trim();
    if (!name) throw new Error("Full name is required.");
    payload.full_name = name;
  }
  if (patch.phone != null) {
    payload.phone = patch.phone.trim();
  }
  if (Object.keys(payload).length === 0) return;
  const { error } = await supabase.from("profiles").update(payload).eq("id", userId);
  if (error) throw error;
}

export async function upsertBorrowerSettings(userId: string, patch: {
  loanLimit?: number;
  verificationLevel?: VerificationLevel;
  expressRate?: number;
  verifiedGuideRate?: number;
  expressRateSchedule?: ExpressRateBand[] | null;
  crestRate?: number;
  sovereignGuideRate?: number;
  terms?: BorrowerTerms;
}): Promise<void> {
  const payload: Json = { user_id: userId };
  if (patch.loanLimit != null) payload.loan_limit = patch.loanLimit;
  if (patch.verificationLevel) payload.verification_level = patch.verificationLevel;
  const expressRate = patch.expressRate ?? patch.crestRate;
  const verifiedGuideRate = patch.verifiedGuideRate ?? patch.sovereignGuideRate;
  if (expressRate != null) payload.crest_rate = expressRate;
  if (verifiedGuideRate != null) payload.sovereign_guide_rate = verifiedGuideRate;
  if (patch.expressRateSchedule !== undefined) {
    payload.express_rate_schedule = patch.expressRateSchedule;
  }
  if (patch.terms) {
    payload.crest_rate = patch.terms.expressRate ?? patch.terms.crestRate;
    payload.sovereign_guide_rate = patch.terms.verifiedGuideRate ?? patch.terms.sovereignGuideRate;
    payload.allow_negotiate = patch.terms.allowNegotiate;
    if (patch.terms.expressRateScheduleOverride !== undefined) {
      payload.express_rate_schedule = patch.terms.expressRateScheduleOverride;
    }
  }
  const { error } = await supabase.from("borrower_settings").upsert(payload, { onConflict: "user_id" });
  if (error) {
    const hint =
      /allow_negotiate|express_rate_schedule|column/i.test(error.message)
        ? " Run supabase/sql/borrower_settings_terms.sql in the Supabase SQL editor."
        : "";
    throw new Error(`${error.message}${hint}`);
  }
}

export async function counterLoanOffer(input: {
  loanId: string;
  borrowerId: string;
  principal: number;
  repay: number;
  durationDays: number;
  note?: string;
}): Promise<void> {
  const days = Math.max(1, Math.round(input.durationDays));
  const dueDate = new Date(Date.now() + days * 86400000).toISOString();
  const rate = input.principal > 0
    ? Number((((input.repay - input.principal) / input.principal) * 100).toFixed(2))
    : 0;
  const { data, error } = await supabase
    .from("loans")
    .update({
      status: "countered",
      counter_principal: input.principal,
      counter_repay: input.repay,
      counter_duration_days: days,
      counter_due_date: dueDate,
      counter_rate: rate,
      counter_note: input.note || null,
    })
    .eq("id", input.loanId)
    .select("id, status")
    .maybeSingle();
  if (error) throw error;
  if (!data || String((data as Json).status) !== "countered") {
    throw new Error("Could not send counter-offer. Please try again.");
  }
  await insertNotification(
    input.borrowerId,
    `A counter-offer is ready on your loan: ${formatNairaSafe(input.principal)} → repay ${formatNairaSafe(input.repay)} in ${formatDurationDays(days)}.`,
    { title: "Loan counter-offer", type: "loan" },
  );
}

function rpcMissingMessage(err: { message?: string }, sqlFile: string): string | null {
  const msg = (err.message ?? "").toLowerCase();
  if (msg.includes("could not find the function") || msg.includes("schema cache")) {
    return `Negotiation actions are not enabled on the server yet. Run ${sqlFile} in Supabase SQL Editor.`;
  }
  return null;
}

export async function acceptLoanCounter(loanId: string, borrowerId: string): Promise<void> {
  const loan = await fetchLoanById(loanId);
  if (!loan || loan.borrowerId !== borrowerId) throw new Error("Loan not found.");
  if (loan.status !== "countered") throw new Error("No counter-offer to accept.");
  if (loan.counterPrincipal == null || loan.counterRepay == null || loan.counterDurationDays == null) {
    throw new Error("Counter-offer is incomplete.");
  }

  const { error: rpcErr } = await supabase.rpc("accept_loan_counter", { p_loan_id: loanId });
  if (rpcErr) {
    const hint = rpcMissingMessage(rpcErr, "loan_negotiation.sql");
    if (hint) {
      // Fallback: direct update (works if RLS allows it)
      const dueDate = loan.counterDueDate
        ?? new Date(Date.now() + loan.counterDurationDays * 86400000).toISOString();
      const gDays = loanGraceDays(loan);
      const { data, error } = await supabase
        .from("loans")
        .update({
          status: "counter_accepted",
          amount_requested: loan.counterPrincipal,
          total_repayable: loan.counterRepay,
          interest_rate: loan.counterRate ?? loan.interestRate,
          duration_days: loan.counterDurationDays,
          duration: `${loan.counterDurationDays}d`,
          due_date: dueDate,
          proposed_repay: loan.counterRepay,
          grace_ends_at: graceEndIso(dueDate, gDays),
        })
        .eq("id", loanId)
        .eq("borrower_id", borrowerId)
        .select("id, status")
        .maybeSingle();
      if (error) throw error;
      if (!data || String((data as Json).status) !== "counter_accepted") {
        throw new Error(hint);
      }
    } else {
      throw new Error(rpcErr.message || "Could not accept the counter-offer.");
    }
  }

  await insertNotification(borrowerId, "You accepted the counter-offer. Waiting for final admin confirmation.", {
    title: "Counter-offer accepted",
    type: "loan",
  });
}

export async function declineLoanCounter(loanId: string, borrowerId: string): Promise<void> {
  const { error: rpcErr } = await supabase.rpc("decline_loan_counter", { p_loan_id: loanId });
  if (rpcErr) {
    const hint = rpcMissingMessage(rpcErr, "loan_negotiation.sql");
    if (hint) {
      const { data, error } = await supabase
        .from("loans")
        .update({ status: "declined", decline_reason: "Borrower declined counter-offer" })
        .eq("id", loanId)
        .eq("borrower_id", borrowerId)
        .select("id, status")
        .maybeSingle();
      if (error) throw error;
      if (!data || String((data as Json).status) !== "declined") {
        throw new Error(hint);
      }
      return;
    }
    throw new Error(rpcErr.message || "Could not decline the counter-offer.");
  }
}

/** Borrower counters admin's offer → loan returns to pending with new proposed terms. */
export async function borrowerCounterOffer(input: {
  loanId: string;
  borrowerId: string;
  principal: number;
  repay: number;
  durationDays: number;
  note?: string;
}): Promise<void> {
  const loan = await fetchLoanById(input.loanId);
  if (!loan || loan.borrowerId !== input.borrowerId) throw new Error("Loan not found.");
  if (loan.status !== "countered") {
    throw new Error("You can only counter when there is an admin counter-offer.");
  }
  const days = Math.max(1, Math.round(input.durationDays));
  if (input.principal <= 0) throw new Error("Enter a valid principal.");
  if (input.repay < input.principal) throw new Error("Repay must be at least the principal.");

  const { error: rpcErr } = await supabase.rpc("borrower_counter_loan", {
    p_loan_id: input.loanId,
    p_principal: input.principal,
    p_repay: input.repay,
    p_duration_days: days,
    p_note: input.note ?? null,
  });
  if (rpcErr) {
    const hint = rpcMissingMessage(rpcErr, "loan_negotiation.sql");
    if (hint) {
      const rate = Number((((input.repay - input.principal) / input.principal) * 100).toFixed(2));
      const dueDate = new Date(Date.now() + days * 86400000).toISOString();
      const { data, error } = await supabase
        .from("loans")
        .update({
          status: "pending",
          amount_requested: input.principal,
          total_repayable: input.repay,
          proposed_repay: input.repay,
          interest_rate: rate,
          duration_days: days,
          duration: `${days}d`,
          due_date: dueDate,
          counter_principal: null,
          counter_repay: null,
          counter_duration_days: null,
          counter_due_date: null,
          counter_rate: null,
          counter_note: input.note || null,
          decline_reason: null,
        })
        .eq("id", input.loanId)
        .eq("borrower_id", input.borrowerId)
        .select("id, status")
        .maybeSingle();
      if (error) throw error;
      if (!data || String((data as Json).status) !== "pending") {
        throw new Error(hint);
      }
    } else {
      throw new Error(rpcErr.message || "Could not send your counter-offer.");
    }
  }

  await insertNotification(
    input.borrowerId,
    `You sent a counter-proposal: ${formatNairaSafe(input.principal)} → repay ${formatNairaSafe(input.repay)} in ${formatDurationDays(days)}. Waiting for CrediKing.`,
    { title: "Your counter sent", type: "loan" },
  );
}

/** Admin final say: apply standing counter terms without waiting for borrower Accept. */
export async function adminApplyLoanCounter(loanId: string): Promise<void> {
  const { error: rpcErr } = await supabase.rpc("admin_apply_loan_counter", { p_loan_id: loanId });
  if (rpcErr) {
    const hint = rpcMissingMessage(rpcErr, "loan_negotiation.sql");
    if (hint) {
      const loan = await fetchLoanById(loanId);
      if (!loan || loan.status !== "countered") throw new Error("Loan is not in countered status.");
      if (loan.counterPrincipal == null || loan.counterRepay == null || loan.counterDurationDays == null) {
        throw new Error("Counter-offer is incomplete.");
      }
      const dueDate = loan.counterDueDate
        ?? new Date(Date.now() + loan.counterDurationDays * 86400000).toISOString();
      const gDays = loanGraceDays(loan);
      const { data, error } = await supabase
        .from("loans")
        .update({
          status: "counter_accepted",
          amount_requested: loan.counterPrincipal,
          total_repayable: loan.counterRepay,
          interest_rate: loan.counterRate ?? loan.interestRate,
          duration_days: loan.counterDurationDays,
          duration: `${loan.counterDurationDays}d`,
          due_date: dueDate,
          proposed_repay: loan.counterRepay,
          grace_ends_at: graceEndIso(dueDate, gDays),
        })
        .eq("id", loanId)
        .select("id, status")
        .maybeSingle();
      if (error) throw error;
      if (!data || String((data as Json).status) !== "counter_accepted") {
        throw new Error(hint);
      }
      return;
    }
    throw new Error(rpcErr.message || "Could not apply counter-offer.");
  }
}

/**
 * Rollover on remaining balance:
 * 1) Remaining outstanding becomes new capital
 * 2) Borrower pays interest on that remaining (Paystack) to unlock
 * 3) New tenure: repayable = remaining + new interest (Verified/Elite may negotiate)
 */
export async function quoteLoanRollover(
  loanId: string,
  borrowerId: string,
  opts: { durationDays?: number; proposedRepay?: number } = {},
): Promise<RolloverQuote> {
  const loan = await fetchLoanById(loanId);
  if (!loan || loan.borrowerId !== borrowerId) throw new Error("Loan not found.");

  const rolloverCheck = canRequestRollover(loan);
  if (!rolloverCheck.ok) throw new Error(rolloverCheck.reason ?? "Rollover is not available.");

  if (!isCarryoverEligible(loan)) {
    throw new Error("Rollover is only available from the due date.");
  }

  const remaining = loanOutstanding(loan);
  if (remaining < 100) throw new Error("Nothing left to roll over — repay the small balance instead.");

  const level = loan.verificationLevel;
  const schedule =
    level === "express" ? await fetchPlatformExpressSchedule() : undefined;
  return buildRolloverQuote(loan, {
    durationDays: opts.durationDays,
    proposedRepay: level === "elite" ? opts.proposedRepay : undefined,
    expressSchedule: schedule,
  });
}

/** @deprecated Prefer initiateRolloverInterestPayment from the Repay page. */
export async function requestLoanCarryover(loanId: string, borrowerId: string): Promise<void> {
  await quoteLoanRollover(loanId, borrowerId);
  throw new Error(
    "Pay the rollover interest on your remaining balance from the Repay page to continue.",
  );
}

/** Start Paystack payment for rollover interest on remaining balance. */
export async function initiateRolloverInterestPayment(input: {
  loanId: string;
  durationDays: number;
  proposedRepay?: number;
}) {
  const days = Math.max(1, Math.round(input.durationDays));
  return invokeFunction<{ payment_link?: string; link?: string; quote?: RolloverQuote }>(
    "initiate-repayment",
    {
      loan_id: input.loanId,
      payment_kind: "rollover_interest",
      duration_days: days,
      ...(input.proposedRepay != null ? { proposed_repay: input.proposedRepay } : {}),
    },
  );
}

/** After Paystack redirect — verify tx and apply repayment / rollover / score unlock. */
export async function finalizePaymentCallback(input: {
  reference?: string;
  transactionId?: string;
  txRef?: string;
}) {
  const reference = String(input.reference || input.txRef || input.transactionId || "").trim();
  return invokeFunction<{
    kind?: string;
    success?: boolean;
    message?: string;
  }>("finalize-payment", {
    reference,
    tx_ref: reference,
    transaction_id: input.transactionId ?? "",
  });
}

function formatNairaSafe(n: number): string {
  return `₦${n.toLocaleString("en-NG", { minimumFractionDigits: 2 })}`;
}

export async function verifyBankAccount(accountNumber: string, bankCode: string) {
  return invokeFunction<{ account_name?: string; data?: { account_name?: string } }>("verify-bank-account", {
    account_number: accountNumber,
    bank_code: bankCode,
  });
}

/** Start a Paystack repayment. Amount may be any partial toward the outstanding balance. */
export async function initiateRepayment(input: {
  loanId: string;
  amount: number;
  repaymentId?: string;
}) {
  const amount = Math.round(Number(input.amount) * 100) / 100;
  if (!input.loanId) throw new Error("loan_id is required");
  if (!Number.isFinite(amount) || amount <= 0) throw new Error("Enter a valid repayment amount.");

  return invokeFunction<{ payment_link?: string; link?: string }>("initiate-repayment", {
    loan_id: input.loanId,
    amount,
    ...(input.repaymentId ? { repayment_id: input.repaymentId } : {}),
  });
}

export async function initiateCreditScoreUnlock() {
  return invokeFunction<{ payment_link?: string; link?: string }>("initiate-credit-score-unlock", {});
}

export async function disburseLoan(loanId: string) {
  return invokeFunction("disburse-loan", { loan_id: loanId });
}

export async function sendReminder(input: {
  borrowerId: string;
  loanId: string;
  channel: "email" | "whatsapp" | "both";
  customMessage?: string;
}) {
  return invokeFunction<{ whatsapp_link?: string }>("send-reminder", {
    borrower_id: input.borrowerId,
    loan_id: input.loanId,
    channel: input.channel,
    custom_message: input.customMessage ?? "",
  });
}

export function paymentLinkFrom(data: { payment_link?: string; link?: string } | null | undefined): string | null {
  if (!data) return null;
  return data.payment_link || data.link || null;
}
